import { MRT_ColumnDef, MRT_PaginationState } from "material-react-table";
import { useState } from "react";
import { Box, Button, Chip, IconButton, Typography } from "@mui/material";
import { Delete, Edit, RemoveRedEye } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { DataTable } from "../../libs/core/components/data-table";
import { 
  useGetMessagesQuery,
  useDeleteMessageMutation
} from "./api/contactUsApi";
import { ConfirmModal } from "../../libs/ui/modal/confirm_modal";
import ContactMessageDetailDialog from "./components/contactMessageDetailDialog";
import { ContactUsMessage } from "./types/ContactUsMessage";

const ContactUsManager: React.FC = () => {
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [detailOpen, setDetailOpen] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<ContactUsMessage | null>(null);
  const navigate = useNavigate();

  const {
    data: messages,
    isLoading,
    isError,
    isFetching
  } = useGetMessagesQuery();

  const [deleteContactMessage] = useDeleteMessageMutation();

  const columns: Array<MRT_ColumnDef<ContactUsMessage>> = [
    {
      header: "Name",
      accessorKey: "full_name",
      size: 150,
    },
    {
      header: "Email",
      accessorKey: "email",
      size: 200,
    },
    {
      header: "Phone",
      accessorKey: "phone_number",
      size: 150,
    },
    {
      header: "Subject",
      accessorKey: "subject",
      size: 150,
      Cell({ row: { original } }) {
        return (
          <Chip 
            label={original.subject} 
            color={original.subject === 'other' ? 'default' : 'primary'}
            variant="outlined"
          />
        );
      },
    },
    {
      header: "Date",
      accessorKey: "created_at",
      size: 120,
      Cell({ row: { original } }) {
        return original.created_at ? (
          <Typography variant="body2">
            {new Date(original.created_at).toLocaleDateString('en-US', {
              month: 'long',
              day: 'numeric',
              year: 'numeric'
            }).replace(',', '/')}
          </Typography>
        ) : null;
      },
    },
  ];

  return (
    <Box>
      <DataTable<ContactUsMessage>
        toolbar={
          <Box display="flex" alignItems="center" width="100%">
            <Box display="flex" gap={3} alignItems="center" flex="1">
              <Typography variant="h6">Contact Messages</Typography>
              <Chip
                color="primary"
                variant="outlined"
                label={`${messages?.data?.length ?? 0} Messages`}
              />
            </Box>
          </Box>
        }
        list={messages?.data ?? []}
        count={messages?.data?.length ?? 0}
        columns={columns}
        getRowId={(row) => row.id?.toString()}
        isLoading={isLoading}
        isSuccess={!isError}
        isError={isError}
        isFetching={isFetching}
        renderRowActions={(row) => (
          <Box display={"flex"} gap={1}>
            <IconButton
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedMessage(row);
                setDetailOpen(true);
              }}
            >
              <RemoveRedEye />
            </IconButton>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setSelectedMessage(row);
                setOpenConfirmModal(true);
              }}
            >
              <Delete color="error" />
            </IconButton>
          </Box>
        )}
        pagination={pagination}
        onPaginationChange={setPagination}
        onRowClick={(row) => {
          setSelectedMessage(row);
          setDetailOpen(true);
        }}
      />

      {selectedMessage && (
        <ContactMessageDetailDialog
          open={detailOpen}
          onClose={() => setDetailOpen(false)}
          message={selectedMessage}
        />
      )}

      {selectedMessage && (
        <ConfirmModal
          type="warning"
          title="Delete Message"
          message={`Are you sure you want to delete message from ${selectedMessage.full_name}?`}
          modalOpen={openConfirmModal}
          setModalOpen={setOpenConfirmModal}
          onOk={() => {
            deleteContactMessage(selectedMessage.id.toString());
            setOpenConfirmModal(false);
            setSelectedMessage(null);
          }}
        />
      )}
    </Box>
  );
};

export default ContactUsManager;